import React from "react";
import { FileCheck2, ShieldCheck } from "lucide-react";
import "./ProductEvidence.css";

const ROOT = "/marketing/product-evidence/v1";
const WIDTHS = Object.freeze([480, 720, 1080, 1440, 2160]);

const asset = (id, width, height, alt, theme) => Object.freeze({
  id,
  width,
  height,
  alt,
  theme,
  variants: Object.freeze(
    [...new Set([...WIDTHS.filter((variantWidth) => variantWidth <= width), width])].map((variantWidth) => Object.freeze({
      width: variantWidth,
      src: `${ROOT}/${id}.w${variantWidth}.webp`,
    })),
  ),
});

const ASSETS = Object.freeze({
  "report-card-preview": asset(
    "report-card-preview",
    2160,
    3054,
    "Sample Vidyapeeth360 academic report card with subject marks, grades, attendance, overall result and a teacher-reviewed remark. It is visibly marked SAMPLE and NOT VALID.",
    "document-light",
  ),
  "holistic-progress-card": asset(
    "holistic-progress-card",
    2160,
    3054,
    "Sample Vidyapeeth360 Holistic Progress Card with scholastic marks, co-scholastic ratings, socio-emotional indicators, attendance and a teacher-reviewed remark. It is visibly marked SAMPLE and NOT VALID.",
    "document-light",
  ),
  "certificate-preview": asset(
    "certificate-preview",
    2160,
    1528,
    "Sample landscape certificate rendered by Vidyapeeth360 with fictional recipient wording, issue reference and signatory lines. It is visibly marked PREVIEW, SAMPLE and NOT VALID.",
    "document-light",
  ),
  "fee-receipt": asset(
    "fee-receipt",
    2160,
    3054,
    "Sample Vidyapeeth360 fee receipt with fictional receipt, invoice and payment references, fee line items and exact paise totals. It is visibly marked SAMPLE and NOT VALID.",
    "document-light",
  ),
  "student-id-card-sheet": asset(
    "student-id-card-sheet",
    2160,
    3054,
    "Sample A4 print sheet rendered by Vidyapeeth360 with two fictional, non-verifiable student identity cards and cut guides. It is visibly marked SAMPLE and NOT VALID and contains no actionable QR code.",
    "document-light",
  ),
  "payslip-preview": asset(
    "payslip-preview",
    2160,
    3054,
    "Sample Vidyapeeth360 payslip with fictional employee context, earnings, deductions, employer contribution and exact paise totals. It is visibly marked SAMPLE and NOT VALID.",
    "document-light",
  ),
  "timetable-workspace-light": asset(
    "timetable-workspace-light",
    2160,
    1203,
    "Vidyapeeth360 Timetable in light theme showing a synthetic Class 8A weekly grid with period times, subjects, assigned staff labels and rooms.",
    "light",
  ),
  "timetable-workspace-dark": asset(
    "timetable-workspace-dark",
    2160,
    1203,
    "Vidyapeeth360 Timetable in dark theme showing the same synthetic Class 8A weekly grid with period times, subjects, assigned staff labels and rooms.",
    "dark",
  ),
  "website-builder-editor-light": asset(
    "website-builder-editor-light",
    2160,
    1581,
    "Vidyapeeth360 Website Builder in light theme showing page navigation, a live fictional school-site preview, section controls and the selected hero inspector.",
    "light",
  ),
  "website-builder-editor-dark": asset(
    "website-builder-editor-dark",
    2160,
    1581,
    "Vidyapeeth360 Website Builder in dark theme showing the same synthetic site, controls and selected hero inspector.",
    "dark",
  ),
  "website-published-light": asset(
    "website-published-light",
    2160,
    1242,
    "Published fictional Vidyapeeth Demo School website in light theme with a photo-free hero, learning pathway cards and admissions navigation.",
    "light",
  ),
  "website-published-dark": asset(
    "website-published-dark",
    2160,
    1242,
    "Published fictional Vidyapeeth Demo School website in dark theme with the same photo-free hero, learning pathway cards and admissions navigation.",
    "dark",
  ),
});

const documentEvidence = (id, label, caption) => Object.freeze({
  id,
  label,
  caption,
  themePolicy: "document-light",
  images: Object.freeze([Object.freeze({ label, light: ASSETS[id] })]),
});

const themedEvidence = (id, label, caption, lightId, darkId) => Object.freeze({
  id,
  label,
  caption,
  themePolicy: "site-theme",
  images: Object.freeze([Object.freeze({ label, light: ASSETS[lightId], dark: ASSETS[darkId] })]),
});

export const PRODUCT_EVIDENCE = Object.freeze({
  "report-card-preview": documentEvidence(
    "report-card-preview",
    "Standard report card output",
    "Rendered by the shipped report-card PDF renderer from a deterministic fictional learner fixture.",
  ),
  "holistic-progress-card": documentEvidence(
    "holistic-progress-card",
    "Holistic Progress Card output",
    "Rendered by the shipped report-card PDF renderer with scholastic and holistic sections from a deterministic fictional learner fixture.",
  ),
  "certificate-preview": documentEvidence(
    "certificate-preview",
    "Certificate output",
    "Rendered by the shipped certificate PDF renderer without a tenant verification URL, so no QR can be generated.",
  ),
  "fee-receipt": documentEvidence(
    "fee-receipt",
    "Fee receipt output",
    "Rendered by the shipped receipt PDF renderer from fictional invoice and collection records.",
  ),
  "student-id-card-sheet": documentEvidence(
    "student-id-card-sheet",
    "Student ID print sheet",
    "Rendered by the shipped ID-card PDF renderer with fictional learners and no tenant verification URL or actionable QR.",
  ),
  "payslip-preview": documentEvidence(
    "payslip-preview",
    "Payslip output",
    "Rendered by the shipped payroll delivery renderer from a fictional employee and exact paise-based sample components.",
  ),
  "timetable-workspace": themedEvidence(
    "timetable-workspace",
    "Timetable workspace",
    "Captured from the shipped Timetable route with a deterministic Class 8A fixture. The capture follows the selected light or dark theme.",
    "timetable-workspace-light",
    "timetable-workspace-dark",
  ),
  "website-builder": Object.freeze({
    id: "website-builder",
    label: "Website Builder and published result",
    caption: "Captured from the shipped Website Builder and Public Site routes using the same fictional, photo-free school fixture. Both captures follow the selected theme.",
    themePolicy: "site-theme",
    images: Object.freeze([
      Object.freeze({ label: "Editor view", light: ASSETS["website-builder-editor-light"], dark: ASSETS["website-builder-editor-dark"] }),
      Object.freeze({ label: "Published result", light: ASSETS["website-published-light"], dark: ASSETS["website-published-dark"] }),
    ]),
  }),
});

const MODULE_EVIDENCE_IDS = Object.freeze({
  "result-publishing": "report-card-preview",
  "report-card-ai": "report-card-preview",
  "certificate-generation": "certificate-preview",
  "receipt-generation": "fee-receipt",
  "id-card-management": "student-id-card-sheet",
  "timetable-management": "timetable-workspace",
  payroll: "payslip-preview",
  "website-builder-ai": "website-builder",
});

const GUIDE_EVIDENCE_IDS = Object.freeze({
  "academic-management": "timetable-workspace",
  "fees-management": "fee-receipt",
  "employee-payroll": "payslip-preview",
  "holistic-progress-card": "holistic-progress-card",
});

export const productEvidenceForModule = (key) => PRODUCT_EVIDENCE[MODULE_EVIDENCE_IDS[key]] || null;
export const productEvidenceForGuide = (key) => PRODUCT_EVIDENCE[GUIDE_EVIDENCE_IDS[key]] || null;

function Picture({ image, mode, sizes }) {
  const srcSet = image.variants.map((variant) => `${variant.src} ${variant.width}w`).join(", ");
  const fallback = image.variants.find((variant) => variant.width === 720) || image.variants[0];
  const full = image.variants[image.variants.length - 1];
  return (
    <a
      className="product-evidence__open"
      data-theme-source={mode}
      href={full.src}
      target="_blank"
      rel="noreferrer"
      aria-label={`Open full-size ${image.alt}`}
    >
      <picture className="product-evidence__picture" data-theme-source={mode}>
        <source type="image/webp" srcSet={srcSet} sizes={sizes} />
        <img
          src={fallback.src}
          alt={image.alt}
          width={image.width}
          height={image.height}
          loading="lazy"
          decoding="async"
          draggable="false"
          data-product-evidence-id={image.id}
          data-image-treatment="responsive-unfiltered-uncropped"
        />
      </picture>
    </a>
  );
}

export function ResponsiveEvidencePicture({ item, sizes }) {
  const portrait = item.light.width <= item.light.height;
  const resolvedSizes = sizes || (portrait
    ? "(max-width: 720px) 92vw, 672px"
    : "(max-width: 720px) 92vw, 1080px");
  return (
    <span
      className="product-evidence__picture-set"
      data-has-dark={item.dark ? "true" : "false"}
      data-orientation={item.light.width > item.light.height ? "landscape" : "portrait"}
    >
      <Picture image={item.light} mode={item.dark ? "light" : "document"} sizes={resolvedSizes} />
      {item.dark ? <Picture image={item.dark} mode="dark" sizes={resolvedSizes} /> : null}
    </span>
  );
}

export function ProductEvidenceOverview({ evidence, testId }) {
  if (!evidence) return null;
  return (
    <figure className="product-evidence product-evidence--guide" data-testid={testId} data-theme-policy={evidence.themePolicy}>
      <header className="product-evidence__header">
        <span><FileCheck2 aria-hidden="true" /> First-party product evidence</span>
        <strong>Synthetic demo fixture · no customer data</strong>
      </header>
      <div className="product-evidence__gallery" data-count={evidence.images.length}>
        {evidence.images.map((item) => (
          <figure className="product-evidence__item" key={item.label}>
            <ResponsiveEvidencePicture
              item={item}
              sizes={item.light.width <= item.light.height
                ? "(max-width: 720px) 92vw, 576px"
                : evidence.images.length > 1
                ? "(max-width: 760px) 92vw, 540px"
                : "(max-width: 720px) 92vw, 1080px"}
            />
            <figcaption><span>{item.label}</span><span>Full-size sample</span></figcaption>
          </figure>
        ))}
      </div>
      <figcaption className="product-evidence__caption"><strong>{evidence.label}.</strong> {evidence.caption}</figcaption>
    </figure>
  );
}

export function ProductEvidencePanel({ evidence, fallback }) {
  if (!evidence) return fallback;
  return (
    <div className="product-evidence" data-testid="module-product-evidence" data-evidence-id={evidence.id} data-theme-policy={evidence.themePolicy}>
      <header className="product-evidence__header">
        <span><FileCheck2 aria-hidden="true" /> First-party product evidence</span>
        <strong>Synthetic demo fixture · no customer data</strong>
      </header>
      <div className="product-evidence__gallery" data-count={evidence.images.length}>
        {evidence.images.map((item) => (
          <figure className="product-evidence__item" key={item.label}>
            <ResponsiveEvidencePicture
              item={item}
              sizes={item.light.width <= item.light.height
                ? "(max-width: 720px) 92vw, 672px"
                : evidence.images.length > 1
                ? "(max-width: 760px) 92vw, 600px"
                : "(max-width: 720px) 92vw, (max-width: 1200px) 82vw, 1080px"}
            />
            <figcaption><span>{item.label}</span><span>Full-size sample</span></figcaption>
          </figure>
        ))}
      </div>
      <p className="product-evidence__caption"><strong>{evidence.label}.</strong> {evidence.caption}</p>
      <details className="product-evidence__fallback">
        <summary><ShieldCheck aria-hidden="true" /> Read the accessible sample fields</summary>
        <div>{fallback}</div>
      </details>
    </div>
  );
}

export { ASSETS as PRODUCT_EVIDENCE_ASSETS, MODULE_EVIDENCE_IDS, GUIDE_EVIDENCE_IDS };
